import {
  type InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { teamService } from '@/services/team-service';
import type { Role } from '@/types/core/role';
import type { Page } from '@/types/shared/page';
import { pollForResourceGone } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { teamMutationKeys, teamQueryKeys } from './query-keys';

export type RemoveTeamRoleArgs = {
  roleId: UUID;
};

/**
 * Hook to remove a role from a team.
 * Optimistically updates the team roles list and polls until the role is gone.
 */
export function useRemoveTeamRole(tenantId?: UUID, teamId?: UUID) {
  const qc = useQueryClient();

  type Context = {
    previousData?: Array<
      [queryKey: readonly unknown[], data: InfiniteData<Page<Role>> | undefined]
    >;
  };

  return useMutation<UUID, Error, RemoveTeamRoleArgs, Context>({
    mutationKey: teamMutationKeys.removeTeamRole(tenantId, teamId),
    mutationFn: async ({ roleId }: RemoveTeamRoleArgs) => {
      if (!tenantId) throw new Error('Missing tenant id');
      if (!teamId) throw new Error('Missing team id');

      await teamService.revokeTeamRole(tenantId, teamId, roleId);

      await pollForResourceGone(() =>
        teamService.findTeamRole(tenantId, teamId, roleId)
      );

      return roleId;
    },
    onMutate: async ({ roleId }: RemoveTeamRoleArgs) => {
      if (!tenantId || !teamId) return { previousData: [] };

      await qc.cancelQueries({
        queryKey: teamQueryKeys.roleListByTeam(tenantId, teamId),
      });

      const previousData = qc.getQueriesData<InfiniteData<Page<Role>>>({
        queryKey: teamQueryKeys.roleListByTeam(tenantId, teamId),
      });

      qc.setQueriesData<InfiniteData<Page<Role>>>(
        { queryKey: teamQueryKeys.roleListByTeam(tenantId, teamId) },
        (old) => {
          if (!old) return old;

          return {
            ...old,
            pages: old.pages.map((page) => {
              const nextItems = page.items.filter(
                (item) => item.roleId !== roleId
              );
              if (nextItems.length === page.items.length) return page;
              return {
                ...page,
                items: nextItems,
                total: Math.max(0, (page.total ?? 0) - 1),
              };
            }),
          };
        }
      );

      return { previousData };
    },
    onError: (_error, _payload, context) => {
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          qc.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: teamQueryKeys.roleListByTeam(tenantId, teamId),
        refetchType: 'all',
      });
    },
  });
}
